class MachineColorConfiguratorComponent extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;

    // 360° viewer elements
    this.viewer = this.querySelector('[data-360-viewer]');
    this.colorSets = this.querySelectorAll('.machine-configurator__360-set');
    this.rotationHint = this.querySelector('.machine-configurator__rotation-hint');

    // Mobile (single image) elements
    this.mobileImageWrapper = this.querySelector('[data-mobile-image-wrapper]');
    this.mobileImage = this.querySelector('[data-mobile-image]');
    this.mqMobile = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;
    this.isMobile = this.mqMobile ? !!this.mqMobile.matches : false;

    // Video elements
    this.videos = this.querySelectorAll('.machine-configurator__360-video');
    this.currentVideo = null;

    // Option elements
    this.colorOptions = this.querySelectorAll('.machine-configurator__color-option');
    this.currentColorName = this.querySelector('[data-current-color]');

    // 360° rotation state
    this.currentColorSet = null;
    this.currentFrame = 0;
    this.totalFrames = 0;
    this.isDragging = false;
    this.activePointerId = null;
    this.lastPointerX = 0;
    this.dragResidualX = 0;
    this.sensitivity = parseInt(this.viewer?.dataset.sensitivity) || 2;

    // Current selections
    this.selectedColor = null;

    // Video scrubbing performance
    this.pendingVideoFrame = null;
    this.pendingSeekRaf = null;
    this.lastSeekTime = null;

    this.init();
  }

  init() {
    this.setupInitialState();
    this.bindEvents();
    this.preloadImages();
    this.bindResponsive();
  }

  bindResponsive() {
    if (!this.mqMobile) return;
    const onChange = () => {
      const wasMobile = this.isMobile;
      this.isMobile = !!this.mqMobile.matches;
      // If we entered mobile, ensure we show the correct mobile image and avoid video work.
      if (!wasMobile && this.isMobile) {
        this.disable360Interactions();
        if (this.selectedColor) this.updateMobileImage(this.selectedColor);
      }
      // If we left mobile, restore 360 viewer state for the selected color.
      if (wasMobile && !this.isMobile) {
        if (this.selectedColor) this.switchColorSet(this.selectedColor);
      }
    };

    // Support older browsers
    try {
      this.mqMobile.addEventListener('change', onChange);
      this._mqCleanup = () => this.mqMobile.removeEventListener('change', onChange);
    } catch (_) {
      this.mqMobile.addListener(onChange);
      this._mqCleanup = () => this.mqMobile.removeListener(onChange);
    }
  }

  setupInitialState() {
    // Set initial color (honor default flag, fallback to first)
    const defaultColorOption = this.querySelector('.machine-configurator__color-option[data-default="true"]') || this.colorOptions[0];
    if (defaultColorOption) {
      this.selectedColor = defaultColorOption.dataset.color;
      this.switchColorSet(this.selectedColor);
      this.colorOptions.forEach(o => o.classList.toggle('active', o === defaultColorOption));
      if (this.currentColorName) {
        this.currentColorName.textContent = defaultColorOption.dataset.colorName;
      }
    }
  }

  bindEvents() {
    // Color option events
    this.colorOptions.forEach(option => {
      option.addEventListener('click', () => {
        this.selectColor(option.dataset.color, option.dataset.colorName);
      });
    });

    // 360° viewer drag events
    if (this.viewer && !this.isMobile) {
      // Prefer Pointer Events (single path for mouse + touch + pen) with safe fallback.
      // Some storefront browsers / WebViews still have flaky PointerEvent support.
      if (window.PointerEvent) {
        this.viewer.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.viewer.addEventListener('pointermove', this.onPointerMove.bind(this));
        this.viewer.addEventListener('pointerup', this.onPointerUp.bind(this));
        this.viewer.addEventListener('pointercancel', this.onPointerUp.bind(this));
      } else {
        this.viewer.addEventListener('mousedown', this.onMouseDown.bind(this));
        this.viewer.addEventListener('mousemove', this.onMouseMove.bind(this));
        this.viewer.addEventListener('mouseup', this.onMouseUp.bind(this));
        this.viewer.addEventListener('mouseleave', this.onMouseUp.bind(this));

        // Touch for mobile
        this.viewer.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false });
        this.viewer.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
        this.viewer.addEventListener('touchend', this.onTouchEnd.bind(this));
        this.viewer.addEventListener('touchcancel', this.onTouchEnd.bind(this));
      }

      // Prevent default drag behavior
      this.viewer.addEventListener('dragstart', (e) => e.preventDefault());
      // Avoid long-press context menu on mobile while scrubbing
      this.viewer.addEventListener('contextmenu', (e) => e.preventDefault());
    }

    // Keyboard navigation
    this.addEventListener('keydown', this.onKeyDown.bind(this));

    if (this.isMobile) this.disable360Interactions();
  }

  disable360Interactions() {
    // Mobile should be "single image only": no scrubbing hint, no video work.
    if (this.rotationHint) this.rotationHint.style.display = 'none';
    if (this.viewer) this.viewer.style.cursor = 'default';
  }

  preloadImages() {
    // On mobile we preload only the initial mobile image (if any)
    if (this.isMobile) {
      const defaultColorOption = this.querySelector('.machine-configurator__color-option[data-default="true"]') || this.colorOptions[0];
      if (defaultColorOption) {
        const set = this.querySelector(`[data-color="${defaultColorOption.dataset.color}"]`);
        const src = set?.dataset?.mobileImage;
        if (src) {
          const img = new Image();
          img.decoding = 'async';
          img.src = src;
        }
      }
      return;
    }

    // Preload only images for the initial/default color set for faster initial load
    // Other color sets will be loaded on demand when selected
    const defaultColorOption = this.querySelector('.machine-configurator__color-option[data-default="true"]') || this.colorOptions[0];
    if (defaultColorOption) {
      const defaultColor = defaultColorOption.dataset.color;
      const defaultSet = this.querySelector(`[data-color="${defaultColor}"]`);
      if (defaultSet) {
        const images = defaultSet.querySelectorAll('img');
        images.forEach(img => {
          if (!img.complete) {
            img.addEventListener('load', () => { /* image loaded */ });
          }
        });
      }
    }
  }

  selectColor(colorValue, colorName) {
    if (this.selectedColor === colorValue) return;

    // 找到当前选中的颜色选项
    const selectedOption = Array.from(this.colorOptions).find(
      option => option.dataset.color === colorValue
    );

    this.colorOptions.forEach(option => {
      option.classList.toggle('active', option.dataset.color === colorValue);
    });

    if (this.currentColorName) {
      this.currentColorName.textContent = colorName;
    }

    this.selectedColor = colorValue;
    this.switchColorSet(colorValue);

    // 触发颜色切换事件，传递 variant_id（即使为空也触发）
    if (selectedOption) {
      const event = new CustomEvent('machineColorChanged', {
        detail: {
          color: colorValue,
          colorName: colorName,
          variantId: selectedOption.dataset.variantId || ''
        },
        bubbles: true
      });
      this.dispatchEvent(event);
    }
  }

  switchColorSet(colorValue) {
    // Mobile: swap a single image only, never load/scrub video.
    if (this.isMobile) {
      this.updateMobileImage(colorValue);
      // Keep 360 sets hidden to avoid accidental video loads.
      this.colorSets.forEach(set => { set.style.display = 'none'; });
      this.videos.forEach(video => { video.style.display = 'none'; });
      this.currentColorSet = null;
      this.currentVideo = null;
      this.totalFrames = 0;
      this.currentFrame = 0;
      return;
    }

    const prevFrame = this.currentFrame;
    this.dragResidualX = 0;
    this.pendingVideoFrame = null;
    this.lastSeekTime = null;
    if (this.pendingSeekRaf) {
      cancelAnimationFrame(this.pendingSeekRaf);
      this.pendingSeekRaf = null;
    }

    // Hide all sets and videos
    this.colorSets.forEach(set => { set.style.display = 'none'; });
    this.videos.forEach(video => { video.style.display = 'none'; });

    // Show selected color set
    const targetSet = this.querySelector(`[data-color="${colorValue}"]`);
    if (targetSet) {
      targetSet.style.display = 'block';
      this.currentColorSet = targetSet;

      const video = targetSet.querySelector('.machine-configurator__360-video');
      if (video) {
        this.currentVideo = video;
        video.style.display = 'block';
        // Only load video when it's selected (lazy loading)
        this.setupVideoScrubbing(video);
        const df = parseInt(this.viewer?.dataset.frames, 10);
        this.totalFrames = Number.isFinite(df) && df > 0 ? df : 36; // simulate frames over duration
      } else {
        this.currentVideo = null;
        const frames = targetSet.querySelectorAll('img');
        this.totalFrames = frames.length;
      }

      if (this.totalFrames > 0) {
        this.currentFrame = Math.min(prevFrame, this.totalFrames - 1);
        this.showFrame(this.currentFrame);
      }
    }
  }

  updateMobileImage(colorValue) {
    if (!this.mobileImage) return;

    const targetSet = this.querySelector(`[data-color="${colorValue}"]`);
    const srcFromSet = targetSet?.dataset?.mobileImage || '';
    const srcFromInitial = this.mobileImage.getAttribute('data-src') || '';
    const nextSrc = srcFromSet || srcFromInitial;

    if (!nextSrc) return;

    // Only set src on mobile to avoid desktop preloading.
    if (this.mobileImage.getAttribute('src') !== nextSrc) {
      this.mobileImage.setAttribute('src', nextSrc);
    }
    // Keep alt in sync with current selection if possible
    const btn = this.querySelector(`.machine-configurator__color-option[data-color="${colorValue}"]`);
    if (btn?.dataset?.colorName) this.mobileImage.setAttribute('alt', btn.dataset.colorName);
  }

  // Rotation handlers
  onPointerDown(e) {
    if (!this.viewer) return;
    // Only primary button / touch contact
    if (e.button != null && e.button !== 0) return;
    this.isDragging = true;
    this.activePointerId = e.pointerId;
    this.lastPointerX = e.clientX;
    this.dragResidualX = 0;
    this.viewer.style.cursor = 'grabbing';
    try { this.viewer.setPointerCapture(e.pointerId); } catch (_) {}
    e.preventDefault();
  }

  onPointerMove(e) {
    if (!this.isDragging || this.totalFrames <= 1) return;
    if (this.activePointerId != null && e.pointerId !== this.activePointerId) return;
    const dx = e.clientX - this.lastPointerX;
    this.rotateByDelta(dx);
    this.lastPointerX = e.clientX;
    e.preventDefault();
  }

  onPointerUp(e) {
    if (!this.viewer) return;
    if (this.activePointerId != null && e.pointerId !== this.activePointerId) return;
    this.isDragging = false;
    this.activePointerId = null;
    this.dragResidualX = 0;
    this.viewer.style.cursor = 'grab';
    try { this.viewer.releasePointerCapture(e.pointerId); } catch (_) {}
  }

  // Fallback handlers for environments without Pointer Events
  onMouseDown(e) {
    if (!this.viewer) return;
    if (e.button != null && e.button !== 0) return;
    this.isDragging = true;
    this.activePointerId = 'mouse';
    this.lastPointerX = e.clientX;
    this.dragResidualX = 0;
    this.viewer.style.cursor = 'grabbing';
    e.preventDefault();
  }

  onMouseMove(e) {
    if (!this.isDragging || this.totalFrames <= 1) return;
    if (this.activePointerId !== 'mouse') return;
    const dx = e.clientX - this.lastPointerX;
    this.rotateByDelta(dx);
    this.lastPointerX = e.clientX;
  }

  onMouseUp() {
    if (!this.viewer) return;
    if (this.activePointerId !== 'mouse') return;
    this.isDragging = false;
    this.activePointerId = null;
    this.dragResidualX = 0;
    this.viewer.style.cursor = 'grab';
  }

  onTouchStart(e) {
    if (!this.viewer) return;
    if (!e.touches || e.touches.length === 0) return;
    const t = e.touches[0];
    this.isDragging = true;
    this.activePointerId = t.identifier;
    this.lastPointerX = t.clientX;
    this.dragResidualX = 0;
    this.viewer.style.cursor = 'grabbing';
    e.preventDefault();
  }

  onTouchMove(e) {
    if (!this.isDragging || this.totalFrames <= 1) return;
    if (!e.touches || e.touches.length === 0) return;
    const t = Array.from(e.touches).find(tt => tt.identifier === this.activePointerId) || e.touches[0];
    const dx = t.clientX - this.lastPointerX;
    this.rotateByDelta(dx);
    this.lastPointerX = t.clientX;
    e.preventDefault();
  }

  onTouchEnd() {
    if (!this.viewer) return;
    this.isDragging = false;
    this.activePointerId = null;
    this.dragResidualX = 0;
    this.viewer.style.cursor = 'grab';
  }

  rotateByDelta(deltaX) {
    const width = this.viewer?.clientWidth || 300;
    const pxPerFrame = width / Math.max(this.totalFrames || 36, 1);
    const threshold = Math.max(2, pxPerFrame / this.sensitivity);
    // Accumulate residual so small deltas aren't lost (makes scrubbing feel smoother)
    this.dragResidualX += deltaX;
    const framesDelta = Math.trunc(this.dragResidualX / threshold);
    if (framesDelta !== 0) this.dragResidualX -= framesDelta * threshold;
    // Reverse direction: right drag (positive deltaX) should rotate left (decrease frame)
    if (framesDelta !== 0) this.rotateToFrame(this.currentFrame - framesDelta);
  }

  rotateToFrame(frameIndex) {
    if (this.totalFrames <= 1) return;
    if (frameIndex >= this.totalFrames) frameIndex = 0; else if (frameIndex < 0) frameIndex = this.totalFrames - 1;
    this.currentFrame = frameIndex;
    this.showFrame(frameIndex);
  }

  showFrame(frameIndex) {
    if (!this.currentColorSet) return;

    if (this.currentVideo) {
      this.scheduleVideoSeek(frameIndex);
    } else {
      const frames = this.currentColorSet.querySelectorAll('img');
      frames.forEach((frame, i) => frame.classList.toggle('active', i === frameIndex));
    }
  }

  scheduleVideoSeek(frameIndex) {
    if (!this.currentVideo) return;
    this.pendingVideoFrame = frameIndex;
    if (this.pendingSeekRaf) return;

    this.pendingSeekRaf = requestAnimationFrame(() => {
      this.pendingSeekRaf = null;
      const video = this.currentVideo;
      if (!video) return;

      const frames = Math.max(this.totalFrames || 36, 1);
      const rawDuration = video.duration;
      const duration = Number.isFinite(rawDuration) && rawDuration > 0 ? rawDuration : 10;
      const tpf = duration / frames;

      const fi = this.pendingVideoFrame ?? 0;
      let t = fi * tpf;
      if (!Number.isFinite(t)) return;

      // Avoid seeking to the exact end (some browsers can stall there)
      const epsilon = 0.001;
      t = Math.max(0, Math.min(t, Math.max(0, duration - epsilon)));

      // Skip redundant seeks
      if (this.lastSeekTime != null && Math.abs(this.lastSeekTime - t) < tpf * 0.2) return;

      try {
        if (typeof video.fastSeek === 'function') video.fastSeek(t);
        else video.currentTime = t;
        this.lastSeekTime = t;
      } catch (_) {
        // Ignore seek errors; next frame will try again
      }
    });
  }

  setupVideoScrubbing(video) {
    video.muted = true;
    video.playsInline = true;
    video.disablePictureInPicture = true;

    const applyReadyState = () => {
      if (this.viewer) this.viewer.removeAttribute('data-loading');
      video.pause();
      try {
        if (typeof video.fastSeek === 'function') video.fastSeek(0);
        else video.currentTime = 0;
        this.lastSeekTime = 0;
      } catch (_) {}
      if (this.currentFrame > 0) this.showFrame(this.currentFrame);
      this.schedulePreloadOtherColors();
    };

    if (video.readyState >= 1) {
      video.preload = 'auto';
      applyReadyState();
      return;
    }

    if (this.viewer) this.viewer.setAttribute('data-loading', '');
    video.preload = 'auto';
    const ensureReady = () => {
      if (video.readyState >= 1) return Promise.resolve();
      return new Promise((resolve, reject) => {
        const to = setTimeout(() => reject(new Error('Video loading timeout')), 8000);
        video.addEventListener('loadedmetadata', () => { clearTimeout(to); resolve(); }, { once: true });
        video.addEventListener('error', () => { clearTimeout(to); reject(new Error('Video loading error')); }, { once: true });
        if (video.readyState === 0) video.load();
      });
    };

    ensureReady().then(() => {
      applyReadyState();
    }).catch((err) => {
      if (this.viewer) this.viewer.removeAttribute('data-loading');
      console.warn('Video setup failed:', err.message, 'falling back to image mode');
      video.style.display = 'none';
      this.currentVideo = null;
      const frames = this.currentColorSet.querySelectorAll('img');
      this.totalFrames = frames.length;
      if (frames.length > 0) this.showFrame(this.currentFrame);
    });
  }

  schedulePreloadOtherColors() {
    if (this.isMobile || !this.selectedColor) return;
    const schedule = window.requestIdleCallback
      ? (fn) => window.requestIdleCallback(fn, { timeout: 1500 })
      : (fn) => setTimeout(fn, 1000);
    schedule(() => this.preloadOtherColorVideos(this.selectedColor));
  }

  preloadOtherColorVideos(excludeColorValue) {
    if (this.isMobile) return;
    this.colorSets.forEach((set) => {
      const colorValue = set.dataset.color;
      if (colorValue === excludeColorValue) return;
      const video = set.querySelector('.machine-configurator__360-video');
      if (!video || video.dataset.videoPrefetched === 'true') return;
      video.dataset.videoPrefetched = 'true';
      video.preload = 'metadata';
      video.load();
    });
  }

  onKeyDown(e) {
    if (this.totalFrames <= 1) return;
    switch (e.key) {
      case 'ArrowLeft': this.rotateToFrame(this.currentFrame - 1); e.preventDefault(); break;
      case 'ArrowRight': this.rotateToFrame(this.currentFrame + 1); e.preventDefault(); break;
    }
  }

  // Public API
  setColor(colorValue) {
    const opt = this.querySelector(`[data-color="${colorValue}"]`);
    const btn = this.querySelector(`.machine-configurator__color-option[data-color="${colorValue}"]`);
    if (btn) this.selectColor(colorValue, btn.dataset.colorName);
    else if (opt) this.selectColor(colorValue, colorValue);
  }

  getCurrentConfiguration() {
    return { color: this.selectedColor, frame: this.currentFrame };
  }

  disconnectedCallback() {
    this.stopAutoRotation();
    if (this.pendingSeekRaf) {
      cancelAnimationFrame(this.pendingSeekRaf);
      this.pendingSeekRaf = null;
    }
    if (this._mqCleanup) this._mqCleanup();
  }

  // Optional auto-rotation
  startAutoRotation(interval = 3000) {
    this.stopAutoRotation();
    this.autoRotationInterval = setInterval(() => {
      if (!this.isDragging && this.totalFrames > 1) this.rotateToFrame(this.currentFrame + 1);
    }, interval);
  }

  stopAutoRotation() {
    if (this.autoRotationInterval) { clearInterval(this.autoRotationInterval); this.autoRotationInterval = null; }
  }
}

customElements.define('machine-color-configurator-component', MachineColorConfiguratorComponent);

// Shopify theme editor integration
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const el = event.target.querySelector('machine-color-configurator-component');
    if (el) setTimeout(() => el.setupInitialState(), 100);
  });

  document.addEventListener('shopify:section:reorder', (event) => {
    const el = event.target.querySelector('machine-color-configurator-component');
    if (el) setTimeout(() => el.setupInitialState(), 100);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const component = event.target.closest('machine-color-configurator-component');
    const block = event.target;
    if (component && block) {
      if (block.classList.contains('machine-configurator__color-option')) block.click();
    }
  });
}

// Prevent image dragging globally within configurator
document.addEventListener('DOMContentLoaded', () => {
  const configurators = document.querySelectorAll('machine-color-configurator-component');
  configurators.forEach(cfg => {
    const images = cfg.querySelectorAll('img');
    images.forEach(img => img.addEventListener('dragstart', (e) => e.preventDefault()));
  });
});